// Moodle-compatible XML generator.
// Reference: https://docs.moodle.org/en/Moodle_XML_format

const escapeXml = (str = '') => String(str)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&apos;');

const textBlock = (raw = '') => `<text><![CDATA[${raw || ''}]]></text>`;

export const generateMoodleXML = (quizTitle, questions) => {
  const parts = [];
  parts.push('<?xml version="1.0" encoding="UTF-8"?>');
  parts.push('<quiz>');

  if (quizTitle) {
    parts.push('  <question type="category">');
    parts.push(`    <category>${textBlock('$course$/' + escapeXml(quizTitle))}</category>`);
    parts.push('  </question>');
  }

  for (const q of questions) {
    parts.push(renderQuestion(q));
  }

  parts.push('</quiz>');
  return parts.join('\n');
};

const renderQuestion = (q) => {
  const name = q.title || 'Question';
  const text = q.question || '';

  switch (q.type) {
    case 'mc-single':
    case 'mc-multiple': {
      const single = q.type === 'mc-single';
      const options = q.options || [];
      const correctSet = single ? [q.correct] : (q.corrects || []);
      const correctCount = single ? 1 : Math.max(correctSet.length, 1);
      const fraction = 100 / correctCount;
      const answers = options.map((opt, i) => {
        const isCorrect = correctSet.includes(i);
        const frac = isCorrect ? fraction.toFixed(5) : '0';
        return `    <answer fraction="${frac}">${textBlock(opt)}</answer>`;
      }).join('\n');
      return [
        '  <question type="multichoice">',
        `    <name>${textBlock(name)}</name>`,
        `    <questiontext format="html">${textBlock(text)}</questiontext>`,
        `    <single>${single ? 'true' : 'false'}</single>`,
        '    <shuffleanswers>1</shuffleanswers>',
        '    <answernumbering>abc</answernumbering>',
        answers,
        '  </question>',
      ].join('\n');
    }
    case 'truefalse': {
      const isTrue = String(q.correct) === 'true';
      return [
        '  <question type="truefalse">',
        `    <name>${textBlock(name)}</name>`,
        `    <questiontext format="html">${textBlock(text)}</questiontext>`,
        `    <answer fraction="${isTrue ? 100 : 0}">${textBlock('true')}</answer>`,
        `    <answer fraction="${isTrue ? 0 : 100}">${textBlock('false')}</answer>`,
        '  </question>',
      ].join('\n');
    }
    case 'short': {
      const answers = (q.answers || []).filter(Boolean).map(a =>
        `    <answer fraction="100">${textBlock(a)}</answer>`).join('\n');
      return [
        '  <question type="shortanswer">',
        `    <name>${textBlock(name)}</name>`,
        `    <questiontext format="html">${textBlock(text)}</questiontext>`,
        '    <usecase>0</usecase>',
        answers,
        '  </question>',
      ].join('\n');
    }
    case 'numerical': {
      return [
        '  <question type="numerical">',
        `    <name>${textBlock(name)}</name>`,
        `    <questiontext format="html">${textBlock(text)}</questiontext>`,
        '    <answer fraction="100">',
        `      ${textBlock(q.answer)}`,
        `      <tolerance>${escapeXml(q.tolerance || '0')}</tolerance>`,
        '    </answer>',
        '  </question>',
      ].join('\n');
    }
    case 'matching': {
      const subs = (q.pairs || []).map(p => [
        '    <subquestion format="html">',
        `      ${textBlock(p.q)}`,
        `      <answer>${textBlock(p.a)}</answer>`,
        '    </subquestion>',
      ].join('\n')).join('\n');
      return [
        '  <question type="matching">',
        `    <name>${textBlock(name)}</name>`,
        `    <questiontext format="html">${textBlock(text)}</questiontext>`,
        '    <shuffleanswers>1</shuffleanswers>',
        subs,
        '  </question>',
      ].join('\n');
    }
    case 'essay': {
      return [
        '  <question type="essay">',
        `    <name>${textBlock(name)}</name>`,
        `    <questiontext format="html">${textBlock(text)}</questiontext>`,
        '    <responseformat>editor</responseformat>',
        '    <responserequired>1</responserequired>',
        '    <responsefieldlines>10</responsefieldlines>',
        '    <attachments>0</attachments>',
        `    <graderinfo format="html">${textBlock(q.sampleAnswer || '')}</graderinfo>`,
        '  </question>',
      ].join('\n');
    }
    default:
      return '';
  }
};
